import { ValidityPeriodInterface } from "../interfaces/ValidityPeriodInterface";
import { ValidityPeriodIconEnum } from "../enums/ValidityPeriodIconEnum";
import { QualificationValidityTypeEnum } from "../enums/QualificationValidityTypeEnum";
import { format, isBefore, parse, startOfDay } from "date-fns";

interface Props {
    validityPeriod?: ValidityPeriodInterface | null;
    validityType?: QualificationValidityTypeEnum | string | null;
    fixedValidUntil?: string | null;
    expiresAt?: string;
    isAlreadyFormatted?: boolean;
}

type ValidityPeriodDisplay = {
    text: string;
    icon: string;
};

const NO_VALIDITY_PERIOD: ValidityPeriodDisplay = {
    text: "Geen geldigheidsduur",
    icon: ValidityPeriodIconEnum.INFINITE,
};

const isExpired = (date: Date): boolean =>
    isBefore(startOfDay(date), startOfDay(new Date()));

const formatExpiresAtLabel = (
    expiresAt: string,
    isAlreadyFormatted = false,
): ValidityPeriodDisplay => {
    const date = isAlreadyFormatted
        ? parse(expiresAt, "dd-MM-yyyy", new Date())
        : new Date(expiresAt);

    const formattedDate = isAlreadyFormatted
        ? expiresAt
        : format(expiresAt, "dd-MM-yyyy");

    const label = isExpired(date) ? "Vervallen op" : "Verloopt op";

    return {
        text: `${label} ${formattedDate}`,
        icon: ValidityPeriodIconEnum.HISTORY,
    };
};

const formatValidityPeriodLabel = ({
    years,
    months,
}: ValidityPeriodInterface): ValidityPeriodDisplay => {
    if (years && months) {
        return {
            text: `Verloopt na ${years} jaar en ${months} maanden`,
            icon: ValidityPeriodIconEnum.HISTORY,
        };
    }

    if (years) {
        return {
            text: `Verloopt na ${years} jaar`,
            icon: ValidityPeriodIconEnum.HISTORY,
        };
    }

    if (months) {
        return {
            text: `Vervalt na ${months} maanden`,
            icon: ValidityPeriodIconEnum.HISTORY,
        };
    }

    return NO_VALIDITY_PERIOD;
};

/**
 * Builds the visible validity label.
 *
 * Priority:
 * 1. Concrete expiry date → "Vervallen op" / "Verloopt op"
 * 2. Fixed valid until date → "Vervallen op" / "Verloopt op"
 * 3. Relative validity period → "Verloopt na X years / months"
 * 4. No validity period
 */
export const getValidityPeriodDisplay = (
    props: Props,
): ValidityPeriodDisplay => {
    const {
        expiresAt,
        isAlreadyFormatted,
        validityPeriod,
        validityType,
        fixedValidUntil,
    } = props;

    if (expiresAt) {
        return formatExpiresAtLabel(expiresAt, isAlreadyFormatted);
    }

    if (
        validityType === QualificationValidityTypeEnum.FIXED_DATE &&
        fixedValidUntil
    ) {
        return formatExpiresAtLabel(fixedValidUntil);
    }

    if (validityPeriod) {
        return formatValidityPeriodLabel(validityPeriod);
    }

    return NO_VALIDITY_PERIOD;
};
